import type { ScopedRoleEntry } from "core/entities/ScopeDefs";
import type { components } from "resources/api-schema";

export type PublicationCategory = WithRequired<
  CategoryDto,
  "id" | "aggregation"
>;

export type NewsEventAggregation =
  components["schemas"]["Fixnetwork.NewsEvents.Categories.NewsEventAggregation"];

export type PublicationCategoryFormValues = {
  isFeatured: boolean;
  currentTranslationTabValue: string;
  aggregation: components["schemas"]["Fixnetwork.NewsEvents.Categories.NewsEventAggregation"];
  translations: CategoryTranslationsFormValues[];
};

export type CategoryTranslationsFormValues = {
  locale: string;
  title: string;
  description: string;
};

export type CategoryCreate =
  components["schemas"]["Fixnetwork.NewsEvents.Categories.CategoryCreateDto"];

export type CategoryDto =
  components["schemas"]["Fixnetwork.NewsEvents.Categories.CategoryDto"];

export type PublicationArticleCreateDto = Omit<
  components["schemas"]["Fixnetwork.NewsEvents.Articles.ArticleCreateDto"],
  "status"
> & { status: ArticleStatus };

export type BannerCreateDto =
  components["schemas"]["Fixnetwork.NewsEvents.Banners.BannerCreateDto"];

export type BannerUpdateDto =
  components["schemas"]["Fixnetwork.NewsEvents.Banners.BannerUpdateDto"];

export type ArticleDto =
  components["schemas"]["Fixnetwork.NewsEvents.Articles.ArticleDto"];

export type ArticleTranslationCreateDto =
  components["schemas"]["Fixnetwork.NewsEvents.ArticleTranslations.ArticleTranslationCreateDto"];

export type BannerFileDto =
  components["schemas"]["Fixnetwork.NewsEvents.Articles.BannerFileDto"];

export type PublicationArticleUpdateDto =
  components["schemas"]["Fixnetwork.NewsEvents.Articles.ArticleUpdateDto"];

export type ArticleTranslationUpdateDto =
  components["schemas"]["Fixnetwork.NewsEvents.ArticleTranslations.ArticleTranslationUpdateDto"];

export type ArticleStatus =
  components["schemas"]["Fixnetwork.NewsEvents.ArticleStatus"];

export type EventSummaryDto =
  components["schemas"]["Fixnetwork.NewsEvents.Articles.EventSummaryDto"];

export type PublicationArticle = WithRequired<ArticleDto, "id" | "status">;

export type PublicationArticleTypeFormValues = {
  communicationType:
    | ""
    | Extract<NewsEventAggregation, "newstype" | "eventtype">;
  category: string;
  label: string;
};

export type PublicationArticlePermissionFormValues = {
  visibleToOtherBrandsAndRegions: boolean;
  corporateScopes: Map<string, ScopedRoleEntry>;
  shopScopes: Map<string, ScopedRoleEntry>;
};

export type BannerFormValue = {
  bannerFile?: BannerFileDto | null;
  altText: string;
  description: string;
};

export type PublicationArticleContentFormValues = {
  banner: BannerFormValue;
  currentTabValue: string;
  translations: {
    includeCallToActionButton: boolean;
    locale: string;
    title: string;
    content: string;
    tags: string[];
    blurb: string;
    buttonText: string;
    /** Format: URL */
    buttonUrl: string;
    banner: BannerFormValue;
  }[];
};

export type PublicationArticleAvailabilityFormValues = {
  availableFrom: Date | null;
  availableTo: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  address: string;
  locationDescription: string;
  isImportant: boolean;
  url: string;
};

export type PublicationArticleFormValues = {
  typeAndPermissions: PublicationArticleTypeFormValues | null;
  content: PublicationArticleContentFormValues | null;
  availability: PublicationArticleAvailabilityFormValues | null;
};

export type PublicationArticleFormValuesTranslation = {
  includeCallToActionButton: boolean;
  locale: string;
  title: string;
  content: string;
  tags: string[];
  blurb: string;
  buttonText: string;
  /** Format: URL */
  buttonUrl: string;
  banner: BannerFormValue;
};

export type PublicationArticleFormValuesBase = {
  categoryId: string;
  labelId: string;
  // permissions
  visibleToOtherBrandsAndRegions: boolean;
  corporateScopes: Map<string, ScopedRoleEntry>;
  shopScopes: Map<string, ScopedRoleEntry>;
  // content
  banner: BannerFormValue;
  currentTabValue: string;
  translations: PublicationArticleFormValuesTranslation[];
  // availability
  isImportant: boolean;
  availableFrom: Date | null;
  availableTo: Date | null;
};

export type PublicationNewsArticleFormValues = PublicationArticleFormValuesBase;
export type PublicationEventArticleFormValues =
  PublicationArticleFormValuesBase & {
    startDate: Date | null;
    endDate: Date | null;
    address: string;
    locationDescription: string;
    url: string;
  };

export const ACCEPTED_BANNER_FILE_TYPES =
  "image/jpeg, image/png, image/webp, image/bmp";
